import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, CheckCircle2, Clock, Eye, EyeOff, Globe, MessageCircle, Palette } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { trackEvent } from "@/lib/ga4";
import { getLandingAttribution } from "@/lib/landingAttribution";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// TODO replace

export default function BusinessOnboardingPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, "business", undefined, fullName);
      // Same GA4 sign_up (the Google Ads "sign_up" conversion) as /signup.
      trackEvent("sign_up", { method: "email", account_type: "business", source: "business_onboarding", ...getLandingAttribution() });
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <MessageCircle className="h-10 w-10 text-indigo-600" />,
      title: "24/7 Automated Support",
      description: "Automate product support for appliances, electronics, vehicles, and more.",
    },
    {
      icon: <BarChart3 className="h-10 w-10 text-indigo-600" />,
      title: "Revenue Intelligence",
      description: "Identify upsell moments like repairs, parts, or replacements—in real time.",
    },
    {
      icon: <CheckCircle2 className="h-10 w-10 text-indigo-600" />,
      title: "Brand Aligned",
      description: "Trained on your manuals, brand language, and service flows.",
    },
    {
      icon: <Clock className="h-10 w-10 text-indigo-600" />,
      title: "Quick Launch",
      description: "Launch your first Genie in under 24 hours.",
    },
  ];

  const brandFeatures = [
    {
      icon: <Palette className="h-6 w-6 text-purple-600" />,
      title: "Custom-Trained",
      text: "Genies are custom-trained using your content, guides, and service language",
    },
    {
      icon: <MessageCircle className="h-6 w-6 text-purple-600" />,
      title: "Total Control",
      text: "Total control over tone of voice, escalation paths, and customer experience",
    },
    {
      icon: <Globe className="h-6 w-6 text-purple-600" />,
      title: "Multilingual",
      text: "Speak your customer's language — literally, with multilingual support",
    },
    {
      icon: <Clock className="h-6 w-6 text-purple-600" />,
      title: "Always Available",
      text: "Consistent support across all time zones, all year round",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative w-full min-h-screen flex items-center justify-center text-white">
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-background.webp"
            alt="Professional business environment"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60 z-10"></div>
        </div>

        <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left side - Hero content */}
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start mb-6">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight">
                  Want to Provide the{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
                    Best Support
                  </span>{" "}
                  to Your Customers?
                </h1>
              </div>

              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Transform your customer support with AI-powered voice genies that work 24/7, boost satisfaction, and
                unlock new revenue opportunities.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Launch in under 24 hours</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Custom-trained on your content</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>24/7 automated support</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <CheckCircle2 className="h-5 w-5 mr-3 text-green-400" />
                  <span>Revenue intelligence built-in</span>
                </div>
              </div>
            </div>

            {/* Right side - Sign up form */}
            <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-2xl relative z-30">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[#222B45] mb-4">Start Your Free Trial</h2>
                <p className="text-lg text-[#6B7280]">Join hundreds of businesses already using Help Genie</p>
              </div>

              <form
                name="helpgenie_business_onboarding"
                id="helpgenie_business_onboarding"
                data-hs-do-not-collect="true"
                onSubmit={handleSubmit}
                className="space-y-6 relative z-40"
              >
                <div>
                  <Label htmlFor="fullName" className="text-[#222B45]">
                    Full Name
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    autoComplete="name"
                    className="mt-1 relative z-50 text-gray-900"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#222B45]">
                    Business Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your business email"
                    required
                    className="mt-1 relative z-50 text-gray-900"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#222B45]">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Create a password (min 6 characters)"
                      required
                      className="mt-1 relative z-50 text-gray-900 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-50"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#222B45]">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      className="mt-1 relative z-50 text-gray-900 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 z-50"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && <div className="text-sm text-red-500 text-center p-3 bg-red-50 rounded-md">{error}</div>}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 text-lg font-semibold"
                >
                  {loading ? "Creating Account..." : "Start Free Trial"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <div className="text-center text-sm text-[#6B7280]">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/signin")}
                    className="text-purple-600 hover:text-purple-700 font-medium"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Turn Every Support Moment Into a Revenue Opportunity
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Help Genie isn't just for answering questions—it's your first line of intelligent, always-on support that
              solves problems, boosts satisfaction, and unlocks new revenue.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg hover:border-indigo-200 transition-all duration-300 text-center transform hover:-translate-y-1"
              >
                <div className="inline-block bg-indigo-100 p-3 rounded-full mb-4">{feature.icon}</div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Features Section */}
      <section className="w-full bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-6">
              Fully Branded.
              <br />
              Fully Trained.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                Fully Yours.
              </span>
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Help Genie wraps around your brand — tone, values, terminology — so every conversation feels like it's
              coming from you, not a bot.
            </p>
          </div>

          <div className="w-full space-y-4">
            {brandFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-4 shadow-sm flex items-center border-l-4 border-purple-500 hover:shadow-md transition-all duration-300"
              >
                <div className="bg-purple-50 p-2 rounded-full mr-4">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="w-full bg-gradient-to-r from-purple-600 to-blue-600 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Transform Your Customer Support?</h2>
          <p className="text-xl text-purple-100 mb-8">
            Join thousands of businesses that have already revolutionized their customer experience with Help Genie.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold"
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
